import { computed, nextTick, Ref, ref } from "vue";
import { showAlert } from "@allwyn/design-system/src/modal-alert/show-alert";
import { useLocalStorage } from "@allwyn/storage/composables/use-storage";
import { eventBus, GlobalEvents } from "@allwyn/utils/global-events";
import { logException } from "@allwyn/utils/logger/use-app-logger";
import { ContactWecomaKeys } from "@allwyn/utils/wecoma/contact-wecoma-keys";
import { WECOMA_CONSTANTS } from "@allwyn/utils/wecoma/wecoma-constants";
import { wecomaKeysReady } from "@allwyn/utils/wecoma/wecoma-state";
import { useNgSession } from "../../ng-session";
import { AuthenticationAnalytics } from "../analytics/analytics-authentication";
import {
    postLoginData,
    postLogout,
    postForgottenPassword,
    postPasswordReset,
    postKeepAlive,
} from "../authentication-api";
import { useMountAuthentication } from "./use-mount-authentication";
import { useMountLogout } from "./use-mount-logout";
import { ngFrameworkLoaded } from "../../ng-integration";
import { AuthenticationState } from "../models/authentication-state";
import { LoginRequest } from "../models/login-request";
import { LoginResponse } from "../models/login-response";

const GENERAL_ERROR_MESSAGE = "Váš požadavek nyní nelze zpracovat. Zkuste to prosím později.";

const isAuthModalOpen = ref(false);
const isLogoutModalOpen = ref(false);
const isConfirmationModalOpen = ref(false);
const loginResponse = ref<LoginResponse | null>(null);

const emailRef = ref("");
const passwordRef = ref<string | null>(null);
const phoneRef = ref<string | null>(null);
const phoneMasked = ref<string | null>(null);
const errorMessage: Ref<string | null> = ref(null);
const infoMessage: Ref<string | null> = ref(null);
const currentState = ref<AuthenticationState>(AuthenticationState.Login);

function safeParseJson(jsonString: string) {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return null;
    }
}

function clearLoginResponse() {
    loginResponse.value = null;
}

function cddAfterLogin(loginResponse: LoginResponse) {
    if (loginResponse.isCDDWidgetLevel1Required) {
        ng_fw.showCddVerification();
    }
}

/**
 * Runs the necessary actions tied RVO after login.
 *
 * Should be the third check (after player-account and self-exclusion) in the after login flow.
 *
 * @returns true if the RVO popup was requested, false otherwise.
 */
function limitedAccountAfterLogin(): boolean {
    const { isLoggedIn } = useNgSession();
    if (!isLoggedIn.value) return false;

    const isLimitedAccount = loginResponse.value?.regulatorGamblingRegistryStatus === "Banned";

    // Limited account (RVO)
    if (isLimitedAccount) {
        const subText =
            "Je nám líto, ale na základě zákona o hazardních hrách vám nadále nemůžeme umožnit online sázení na herním portálu Allwyn.cz. Výběry z vašeho účtu jsou možné.";

        showAlert({
            title: "Omezení online hraní",
            subText: subText,
            btnConfirmText: "Více informací",
            btnConfirmHref: "/centrum-podpory/online-hrani-na-sazka-cz/muj-ucet/rejstrik-vyloucenych-osob",
            btnCloseText: "Rozumím",
            btnCloseType: "secondary",
            buttonsFlexDirection: "row",
            isWide: true,
            animate: true,
        });

        AuthenticationAnalytics.pushPlayerLimitedAccess(subText);
    }

    return isLimitedAccount;
}

function loginAborted() {
    eventBus.emit(GlobalEvents.WebLoginAborted);
}

async function keepSessionAlive() {
    if (environment?.webView?.isAppWebView) {
        return;
    }

    await ngFrameworkLoaded();
    const { isLoggedIn } = useNgSession();
    if (!isLoggedIn.value) {
        return;
    }

    try {
        await postKeepAlive();
    } catch {
        // no need to inform user if keep alive fails
    }
}

export function useAuthentication() {
    //#region General
    function initializeInterface() {
        if (!window.authentication) {
            window.authentication = {
                ngLoginApi: {
                    success: null,
                },
                openLoginModal,
            };
        }
    }

    async function openLoginModal(useLoader = true) {
        eventBus.emit(GlobalEvents.WebLoginStarted);

        await ngFrameworkLoaded();

        await useMountAuthentication(useLoader);

        clearLoginData();

        nextTick(() => {
            isAuthModalOpen.value = true;
        });
    }

    function clearLoginData() {
        emailRef.value = "";
        passwordRef.value = "";
        phoneRef.value = "";
        phoneMasked.value = null;
        errorMessage.value = null;
        infoMessage.value = null;
        currentState.value = AuthenticationState.Login;
    }

    const countdown = ref(0);
    const smsInfo = computed(
        () =>
            "Znovu jsme vám poslali kód na registrované telefonní číslo. Další nový kód může být zaslán za " +
            countdown.value +
            " s."
    );

    function startCountdown() {
        if (countdown.value > 0) return;
        countdown.value = 15;
        const interval = setInterval(() => {
            countdown.value--;
            infoMessage.value = smsInfo.value;
            if (countdown.value <= 0) {
                clearInterval(interval);
                infoMessage.value = null;
            }
        }, 1000);
    }

    function handleAuthenticationError(error: any, errorInfo: string) {
        loginResponse.value = null;

        logException("Authentication error", {
            errorMessage: errorInfo,
            error: error,
        });

        const errorResponse = error.message?.response?.data ?? error.message;
        const errorArray = safeParseJson(errorResponse);
        AuthenticationAnalytics.pushLoginFailure();

        if (!errorArray || !Array.isArray(errorArray) || errorArray.length === 0) {
            errorMessage.value = GENERAL_ERROR_MESSAGE;
            return;
        }

        if (errorArray[0] === "MISSING_DATA") {
            handleMissingData(errorArray);
            return;
        }

        const newErrorMessage = errorArray[1] ?? null;

        if (errorArray[0] === "EXPIRED_OTP" && newErrorMessage) {
            setState(AuthenticationState.LoginOtp, newErrorMessage);
            return;
        }

        errorMessage.value = newErrorMessage ?? GENERAL_ERROR_MESSAGE;
    }

    function setState(state: AuthenticationState | null = null, error: string | null = null) {
        currentState.value = state ?? currentState.value;
        errorMessage.value = error;
    }
    //#endregion

    //#region Login
    async function login(
        email: string,
        password: string,
        verificationCode: string | null = null,
        newPassword: string | null = null
    ) {
        emailRef.value = email;
        passwordRef.value = password;

        errorMessage.value = null;
        infoMessage.value = null;

        try {
            const loginRequest = createLoginRequest(email, password, verificationCode, newPassword);
            const response = await postLoginData(loginRequest);
            loginResponse.value = response;

            await window.authentication.ngLoginApi.success(
                response.sessionToken,
                response.playerId,
                response.isPostLoginPopupRequired,
                response.bonusPolicyId
            );
            loginSuccess(response.playerId);
            cddAfterLogin(response);

            const storage = useLocalStorage();
            storage.setJson<ContactWecomaKeys>(WECOMA_CONSTANTS.WecomaKeyStorage, response.wecomaKeys, "essential");
            wecomaKeysReady.value = true;
        } catch (error) {
            handleAuthenticationError(error, "Login error");
        }
    }

    function createLoginRequest(
        email: string,
        password: string,
        verificationCode: string | null = null,
        newPassword: string | null = null
    ): LoginRequest {
        const loginRequest: LoginRequest = {
            email: email,
            password: password,
        };

        if (currentState.value === AuthenticationState.LoginOtp) {
            loginRequest.challenges = {
                oneTimePassword: verificationCode ?? undefined,
            };
        }

        if (
            currentState.value === AuthenticationState.ForcedPasswordReset ||
            currentState.value === AuthenticationState.ForcedPasswordResetOtp
        ) {
            loginRequest.challenges = {
                oneTimePassword: verificationCode ?? undefined,
                newPassword: newPassword ?? undefined,
            };
        }

        return loginRequest;
    }

    function loginSuccess(playerId: string) {
        isAuthModalOpen.value = false;
        clearLoginData();

        eventBus.emit(GlobalEvents.WebLoginSuccess);
        AuthenticationAnalytics.pushLoginSuccess(playerId);
    }

    async function resendLoginOtp() {
        if (countdown.value > 0) return;

        try {
            if (!emailRef.value || !passwordRef.value)
                throw new Error("Email and password must be provided for OTP resend request");

            await login(emailRef.value, passwordRef.value);
            if (!errorMessage.value) {
                startCountdown();
                infoMessage.value = smsInfo.value;
            }
        } catch (error) {
            handleAuthenticationError(error, "Resend OTP error");
        }
    }

    function handleMissingData(error: string[]) {
        const additionalInfo = error[2] || null;
        if (additionalInfo) {
            const additionalInfoJson = safeParseJson(additionalInfo);

            const isOtpRequired: boolean = additionalInfoJson.MissingData.includes("OneTimePassword");
            const isNewPasswordRequired: boolean = additionalInfoJson.MissingData.includes("NewPassword");

            if (isOtpRequired && isNewPasswordRequired) {
                phoneMasked.value = additionalInfoJson.PhoneNumber;
                setState(AuthenticationState.ForcedPasswordResetOtp);
            } else if (isNewPasswordRequired) {
                setState(AuthenticationState.ForcedPasswordReset);
            } else if (isOtpRequired) {
                phoneMasked.value = additionalInfoJson.PhoneNumber;
                setState(AuthenticationState.LoginOtp);
            }
        }
    }
    //#endregion

    //#region Password reset
    function showForgottenPassword() {
        setState(AuthenticationState.ForgottenPassword);
    }

    async function forgottenPassword(email: string, phone: string | null) {
        try {
            if (!email || !phone)
                throw new Error("Email and phone number must be provided for forgotten password request");

            const phoneData = "420" + phone?.replaceAll(" ", "");
            emailRef.value = email;
            phoneRef.value = phone;
            errorMessage.value = null;
            infoMessage.value = null;

            await postForgottenPassword({ email: email, mobile: phoneData });
            passwordRef.value = null;
            currentState.value = AuthenticationState.PasswordReset;
        } catch (error) {
            handleAuthenticationError(error, "Forgotten password error");
        }
    }

    async function passwordResetResend() {
        if (isConfirmationModalOpen.value) return;
        if (countdown.value > 0) return;

        try {
            await forgottenPassword(emailRef.value, phoneRef.value);
            if (!errorMessage.value) {
                startCountdown();
                infoMessage.value = smsInfo.value;
            }
        } catch (error) {
            handleAuthenticationError(error, "Resend password reset error");
        }
    }

    async function passwordReset(email: string, newPassword: string, verificationCode: string) {
        if (isConfirmationModalOpen.value) return;

        try {
            await postPasswordReset({
                email: email,
                newPassword: newPassword,
                challenges: { oneTimePassword: verificationCode },
            });
            errorMessage.value = null;
            infoMessage.value = null;
            passwordRef.value = null;

            openConfirmationModal();
        } catch (error) {
            handleAuthenticationError(error, "Password reset error");
        }
    }
    //#endregion

    // #region Confirmation Modal
    function openConfirmationModal() {
        isConfirmationModalOpen.value = true;
    }

    function closeConfirmationModal() {
        isConfirmationModalOpen.value = false;
        currentState.value = AuthenticationState.Login;
    }
    // #endregion

    // #region Logout
    async function logout() {
        await useMountLogout();
        clearLoginData();
        clearLoginResponse();

        nextTick(() => {
            isLogoutModalOpen.value = true;
            isAuthModalOpen.value = false;
        });
        try {
            await postLogout();
        } finally {
            ng_fw.postLogout();
        }
    }
    // #endregion

    return {
        isAuthModalOpen,
        isLogoutModalOpen,
        isConfirmationModalOpen,
        email: emailRef,
        password: passwordRef,
        phoneMasked,
        currentState,
        initializeInterface,
        login,
        loginAborted,
        resendLoginOtp,
        showForgottenPassword,
        forgottenPassword,
        passwordReset,
        passwordResetResend,
        openLoginModal,
        logout,
        clearLoginData,
        closeConfirmationModal,
        limitedAccountAfterLogin,
        errorMessage,
        infoMessage,
        keepSessionAlive,
    };
}
